import React, { useEffect, useRef } from 'react';
import { applePodcastIcon } from '../../icons/ApplePodcastIcon';
import { spotifyPodcastIcon } from '../../icons/SpotifyPodcastIcon';
import { useEventListener } from '../../hooks/useEventListener';
import { useTracking } from '../../../lib/analyticsEvents';
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

interface PostsPodcastPlayerProps {
  podcastEpisode: Exclude<PostPodcastEpisode['podcastEpisode'], null>
  postId: string
  hideIconList?: boolean
  onLoadError?: (episodeLink: string) => void
}

const styles = defineStyles('PostsPodcastPlayer', (theme: ThemeType) => ({
  embeddedPlayer: {
    marginBottom: '2px',
    opacity: theme.dark ? 0.85 : 1,
  },
  podcastIconList: {
    paddingLeft: '0px',
    marginTop: '0px'
  },
  podcastIcon: {
    display: 'inline-block',
    marginRight: '8px'
  }
}));

const PostsPodcastPlayer = ({podcastEpisode, postId, hideIconList = false, onLoadError}: PostsPodcastPlayerProps) => {
  const classes = useStyles(styles);
  const mouseOverDiv = useRef(false);
  const divRef = useRef<HTMLDivElement | null>(null);
  const { captureEvent } = useTracking();

  // Embed a reference to the generated-per-episode buzzsprout script, which is
  // responsible for hydrating the player div (with the id
  // `buzzsprout-player-${externalEpisodeId}`).
  useEffect(() => {
    const newScript = document.createElement('script');
    newScript.async=true;
    // Imported embed URLs can contain HTML-escaped query separators, which
    // assigning to the DOM property does not decode like parsing HTML would.
    newScript.src=podcastEpisode.episodeLink.replace(/&amp;/g, '&');
    newScript.onerror = () => {
      onLoadError?.(podcastEpisode.episodeLink);
    };
    document.head.appendChild(newScript);
    
    return () => {
      newScript.onerror = null;
      newScript.parentNode?.removeChild(newScript);
    }
  }, [onLoadError, podcastEpisode.episodeLink]);

  const setMouseOverDiv = (isMouseOver: boolean) => {
    mouseOverDiv.current = isMouseOver;
  };

  // Dumb hack to let us figure out when the iframe inside the div was clicked on, as a (fuzzy) proxy for people clicking the play button
  // Inspiration: https://gist.github.com/jaydson/1780598
  // This won't trigger more than once per page load, unless the user clicks outside the div element, which will reset it
  useEventListener('blur', (e) => {
    if (mouseOverDiv.current) {
      captureEvent('clickInsidePodcastPlayer', { postId, externalEpisodeId: podcastEpisode.externalEpisodeId, playerType: "buzzSprout" });
    }
  });

  return <>
    <div
      id={`buzzsprout-player-${podcastEpisode.externalEpisodeId}`}
      className={classes.embeddedPlayer}
      ref={divRef}
      onMouseOver={() => setMouseOverDiv(true)}
      onMouseOut={() => setMouseOverDiv(false)}
    />
    {!hideIconList && <ul className={classes.podcastIconList}>
      {podcastEpisode.podcast.applePodcastLink && <li className={classes.podcastIcon}><a href={podcastEpisode.podcast.applePodcastLink}>{applePodcastIcon}</a></li>}
      {podcastEpisode.podcast.spotifyPodcastLink && <li className={classes.podcastIcon}><a href={podcastEpisode.podcast.spotifyPodcastLink}>{spotifyPodcastIcon}</a></li>}
    </ul>}
  </>;
};

export default PostsPodcastPlayer

